import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useApp } from '../context/AppContext';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const prefixToCountry = {
  "+57": { code: "CO", name: "Colombia" },
  "+58": { code: "VE", name: "Venezuela" },
  "+507": { code: "PA", name: "Panamá" },
  "+1": { code: "US", name: "Estados Unidos" },
};

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { startPasswordReset } = useApp();

  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendCode = async () => {
    if (!phone.trim()) {
      setMessage("El número de teléfono es obligatorio.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      await startPasswordReset(phone.trim());

      setTimeout(() => {
        navigate("/verify-reset-code");
      }, 600);
    } catch (err) {
      setMessage(err.message || "Ocurrió un error inesperado.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen px-6 py-12 bg-gray-50">
      <div className="max-w-md mx-auto">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
          className="flex items-center mb-10"
        >
          <button
            onClick={() => navigate('/login')}
            className="p-3 rounded-full hover:bg-gray-200 transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-gray-700" />
          </button>
          <h1 className="text-2xl font-bold ml-4 text-gray-800">Recuperar Contraseña</h1>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white rounded-3xl shadow-lg p-8"
        >
          <h2 className="text-xl font-semibold text-center text-gray-800 mb-3">
            Ingresa tu número de teléfono
          </h2>
          <p className="text-center text-gray-600 mb-8 leading-relaxed">
            Te enviaremos un código de 4 dígitos por WhatsApp para verificar tu identidad y poder restablecer tu contraseña.
          </p>

          {message && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center text-red-600 font-medium mb-6"
            >
              {message}
            </motion.p>
          )}

          <div className="mb-8">
            <PhoneInput
              country="co"
              value={phone}
              onChange={(value) => {
                const formatted = value.startsWith('+') ? value : `+${value}`;
                setPhone(formatted);
              }}
              inputStyle={{
                width: '100%',
                borderRadius: '16px',
                height: '60px',
                fontSize: '18px',
                paddingLeft: '70px',
                border: '2px solid #e5e7eb',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              }}
              buttonStyle={{
                borderRadius: '16px 0 0 16px',
                border: '2px solid #e5e7eb',
                borderRight: 'none',
                background: 'white',
              }}
              dropdownStyle={{
                borderRadius: '12px',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
              }}
              enableSearch
              searchPlaceholder="Buscar país..."
              placeholder="Número de teléfono"
              disabled={loading}
            />
          </div>

          <button
            onClick={handleSendCode}
            disabled={loading}
            className="w-full bg-blue-900 hover:bg-blue-800 text-white font-bold py-4 rounded-2xl transition-all duration-200 disabled:bg-blue-600 disabled:cursor-not-allowed shadow-md"
          >
            {loading ? "Enviando código..." : "Enviar código por WhatsApp"}
          </button>

          <p className="text-center text-xs text-gray-500 mt-6">
            El código tiene una validez de 5 minutos.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default ForgotPassword;
